import { useEffect, useState } from "react";
import LandToursApi from "../../../../api-services/land-tours-api.service";
import { PaxCounts } from "../components/ChooseYourTour";

export interface BookingQuoteRow {
  category: string;
  amount: number;
  free_of_charge: boolean;
}

export interface BookingQuote {
  rows: BookingQuoteRow[];
  groupCharges: number;
  groupSurcharges: number;
  total: number;
}

interface Args {
  landTourId: number | undefined;
  categoryId: number | undefined;
  departDate: string | undefined;
  paxCounts: PaxCounts;
  totalRooms: number;
  nationalities?: string[];
  enabled?: boolean;
}

// Customer total from the same calculators the booking is created with
// (POST .../calculate_booking_price), so the sidebar, footer and breakdown agree with the amount charged.
export default function useBookingQuote({
  landTourId, categoryId, departDate, paxCounts, totalRooms, nationalities = [], enabled = true,
}: Args) {
  const [quote, setQuote] = useState<BookingQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const paxKey = JSON.stringify(paxCounts);
  const nationalityKey = nationalities.join("|");

  useEffect(() => {
    if (!enabled || !landTourId || !categoryId || !departDate) {
      setQuote(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setFailed(false);
    LandToursApi.calculateBookingPrice(landTourId, {
      category_id: categoryId,
      depart_date: departDate,
      total_rooms: totalRooms,
      booking_prices: Object.entries(paxCounts).filter(([, quantity]) => quantity > 0).map(([name, quantity]) => ({ name, quantity })),
      nationalities: nationalities.map((value) => value || "Malaysia"),
    })
      .then((data) => {
        if (!active) return;
        setQuote({
          rows: data.rows || [],
          groupCharges: Number(data.group_charges) || 0,
          groupSurcharges: Number(data.group_surcharges) || 0,
          total: Number(data.total) || 0,
        });
      })
      .catch(() => {
        if (!active) return;
        setQuote(null);
        setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [enabled, landTourId, categoryId, departDate, totalRooms, paxKey, nationalityKey]);

  return { quote, loading, failed };
}
